import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PendingApprovalCard = ({ user, onApprove, onReject, onViewDetails }) => {
  // Early return if no user data
  if (!user) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="CheckCircle2" size={48} color="var(--color-success)" className="mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">All caught up!</h3>
        <p className="text-sm text-muted-foreground">No pending approvals at this time</p>
      </div>
    );
  }

  const getSpecialtyIcon = (specialty) => {
    const icons = {
      'Radiology': 'Scan',
      'Emergency Medicine': 'AlertCircle',
      'General Practice': 'Stethoscope',
      'Internal Medicine': 'Heart',
      'Surgery': 'Scissors'
    };
    return icons?.[specialty] || 'User';
  };

  const getDaysWaiting = (date) => {
    if (!date) return 0;
    const now = new Date();
    const registered = new Date(date);
    const days = Math.floor((now - registered) / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysWaiting = getDaysWaiting(user?.registrationDate);
  const isUrgent = daysWaiting > 3;

  return (
    <div className={`bg-card border rounded-lg p-4 transition-clinical hover:shadow-md ${
      isUrgent ? 'border-warning' : 'border-border'
    }`}>
      <div className="flex items-start gap-4">
        <div className="relative">
          <Image
            src={user?.profileImage}
            alt={user?.profileImageAlt}
            className="w-16 h-16 rounded-lg object-cover"
          />
          {user?.licenseVerified && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center border-2 border-card">
              <Icon name="CheckCircle2" size={14} color="white" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h3 className="text-base font-semibold text-foreground">{user?.name}</h3>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            {isUrgent && (
              <span className="status-badge status-badge-warning flex items-center gap-1">
                <Icon name="Clock" size={12} />
                {daysWaiting}d waiting
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="Phone" size={14} color="var(--color-primary)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Phone</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.phoneNumber || 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="FileText" size={14} color="var(--color-primary)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">License #</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.licenseNumber || 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={14} color="var(--color-primary)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Licensed State</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.licensedState || 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name={getSpecialtyIcon(user?.specialty)} size={14} color="var(--color-primary)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Department</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.specialty || 'Not specified'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="Building2" size={14} color="var(--color-accent)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Institution</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.institution || 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Location</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.location || 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="Building" size={14} color="var(--color-muted-foreground)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Institution Type</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.institutionType || 'Not specified'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={14} color="var(--color-primary)" />
              <div className="min-w-0">
                <span className="text-xs text-muted-foreground block">Years of Practice</span>
                <span className="text-sm text-foreground font-medium block truncate">
                  {user?.yearsOfPractice ? `${user?.yearsOfPractice} years` : 'Not provided'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-muted-foreground">Registered:</span>
            <span className="text-xs font-medium text-foreground">
              {new Date(user.registrationDate)?.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              iconName="CheckCircle2"
              iconPosition="left"
              onClick={() => onApprove(user?.id)}
            >
              Approve
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="XCircle"
              iconPosition="left"
              onClick={() => onReject(user?.id)}
            >
              Reject
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewDetails(user)}
            >
              Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalCard;